import { CLINIC } from "@/data/clinic";

const BASE = typeof window !== "undefined" ? window.location.origin : "https://katiafalonodontologia.com";

// Coordenadas aproximadas de Armenia, Quindío (centro). Cuando la Dra. confirme
// la ubicación exacta del consultorio, actualizar latitude/longitude aquí.
const GEO_COORDS = { latitude: 4.5342, longitude: -75.6811 };

/**
 * Dentist / MedicalClinic con:
 * - medicalSpecialty priorizando odontopediatría y sedación consciente
 * - availableService con las 6 áreas y las 3 reservables online
 * - geo, areaServed extendido a municipios cercanos
 * - keywords, slogan y sameAs
 */
export const dentistJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": ["Dentist", "MedicalClinic"],
  "@id": `${BASE}/#organization`,
  name: CLINIC.name,
  alternateName: "Katia Falón — Odontología sin dolor",
  url: BASE,
  telephone: CLINIC.phone,
  email: CLINIC.email,
  image: `${BASE}/logo-katia.png`,
  logo: `${BASE}/logo-katia.png`,
  slogan: "Odontología sin dolor en Armenia, Quindío",
  description:
    "Odontología sin dolor y odontopediatría en Armenia, Quindío. Atención personalizada con anestesia electrónica, planes de tratamiento claros y ambiente amable para adultos y niños.",
  keywords: [
    "odontología sin dolor en Armenia",
    "odontopediatría en Armenia",
    "dentista para niños en Armenia",
    "odontóloga en Armenia Quindío",
    "higiene oral en Armenia",
    "valoración odontológica en Armenia",
    "odontología infantil en Armenia",
  ].join(", "),
  address: {
    "@type": "PostalAddress",
    streetAddress: "Cra. 14 #17N-32",
    addressLocality: "Armenia",
    addressRegion: "Quindío",
    postalCode: "630004",
    addressCountry: "CO",
  },
  geo: { "@type": "GeoCoordinates", latitude: GEO_COORDS.latitude, longitude: GEO_COORDS.longitude },
  hasMap: `https://maps.google.com/maps?q=${CLINIC.mapsQuery}`,
  areaServed: [
    { "@type": "City", name: "Armenia" },
    { "@type": "AdministrativeArea", name: "Quindío" },
    { "@type": "City", name: "Calarcá" },
    { "@type": "City", name: "La Tebaida" },
    { "@type": "City", name: "Circasia" },
  ],
  sameAs: [
    CLINIC.instagram,
    `https://www.google.com/search?q=${encodeURIComponent(CLINIC.name + " Armenia Quindío")}`,
  ].filter(Boolean),
  openingHoursSpecification: [
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday"], opens: "08:00", closes: "18:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Saturday"], opens: "08:00", closes: "13:30" },
  ],
  medicalSpecialty: [
    "Dentistry",
    "PediatricDentistry",
    "CosmeticProcedure",
  ],
  availableService: [
    { "@type": "MedicalTherapy", name: "Odontología sin dolor", alternateName: "Painless Dentistry", description: "Manejo del dolor y de la ansiedad odontológica con anestesia electrónica y — cuando aplica — sedación consciente supervisada." },
    { "@type": "MedicalTherapy", name: "Odontopediatría", alternateName: "Pediatric Dentistry", description: "Atención dental para bebés, niños y adolescentes en Armenia, Quindío, con enfoque amable y explicaciones adaptadas a su edad." },
    { "@type": "MedicalTherapy", name: "Higiene oral", description: "Profilaxis y limpieza dental profesional en Armenia. Prevención de caries y enfermedad periodontal." },
    { "@type": "MedicalTherapy", name: "Estética dental", description: "Diseño de sonrisa, blanqueamiento y carillas en Armenia, Quindío." },
    { "@type": "MedicalTherapy", name: "Ortodoncia", description: "Brackets y alineadores transparentes en Armenia." },
    { "@type": "MedicalTherapy", name: "Implantes dentales", description: "Implantes dentales oseointegrados en Armenia, Quindío." },
    { "@type": "MedicalTherapy", name: "Odontología general", description: "Diagnóstico, obturaciones estéticas y endodoncia en Armenia." },
  ],
  employee: {
    "@type": "Physician",
    name: "Dra. Katia Falón",
    jobTitle: "Odontóloga",
    medicalSpecialty: ["Dentistry", "CosmeticProcedure"],
    alumniOf: [
      { "@type": "CollegeOrUniversity", name: "Universidad Autónoma de Manizales" },
      { "@type": "CollegeOrUniversity", name: "Universidad del Área Andina" },
      { "@type": "CollegeOrUniversity", name: "Universidad del Valle" },
    ],
    hasCredential: [
      { "@type": "EducationalOccupationalCredential", credentialCategory: "degree", name: "Odontología" },
      { "@type": "EducationalOccupationalCredential", credentialCategory: "specialization", name: "Especialista en Gerencia de Salud" },
      { "@type": "EducationalOccupationalCredential", credentialCategory: "diploma", name: "Diplomado en Biomimética y Estética en el Sector Anterior" },
    ],
  },
  paymentAccepted: "Efectivo, transferencia, tarjeta de crédito, tarjeta débito",
  currenciesAccepted: "COP",
  knowsLanguage: ["es", "en"],
});

export const treatmentJsonLd = (t) => ({
  "@context": "https://schema.org",
  "@type": "MedicalProcedure",
  name: t.name,
  description: t.description,
  procedureType: "Dental",
  bodyLocation: "Cavidad oral",
  provider: { "@id": `${BASE}/#organization` },
  ...(t.slug === "odontologia-sin-dolor" && { alternateName: ["Painless Dentistry", "Odontología sin dolor en Armenia"] }),
  ...(t.slug === "odontopediatria" && { alternateName: ["Pediatric Dentistry", "Odontopediatría en Armenia", "Dentista para niños en Armenia"] }),
});

export const faqJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${BASE}${it.path}`,
  })),
});

export const articleJsonLd = (post) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: post.title,
  description: post.excerpt,
  author: { "@type": "Organization", name: post.author },
  datePublished: post.created_at,
  dateModified: post.updated_at,
  publisher: { "@id": `${BASE}/#organization` },
  ...(post.cover_slug && { image: `${BASE}/images/${post.cover_slug}.jpg` }),
  mainEntityOfPage: `${BASE}/blog/${post.slug}`,
});

// WebSite + Sitelinks Searchbox — para AEO y búsquedas por voz
export const websiteJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: CLINIC.name,
  url: BASE,
  potentialAction: {
    "@type": "SearchAction",
    target: `${BASE}/blog?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});
